import { cn } from '@undp/design-system-react/cn';
import { H3 } from '@undp/design-system-react/Typography';

import { GraphFooter } from '@/Components/Elements/GraphFooter';
import { GraphHeader } from '@/Components/Elements/GraphHeader';
import type {
  ClassNameObject,
  Languages,
  NumberFormatOptions,
  SourcesDataType,
  StyleObject,
} from '@/Types';
import { numberFormattingFunction } from '@/Utils/numberFormattingFunction';

interface Props {
  // Titles, Labels, and Sources
  /** Title of the graph */
  graphTitle?: string | React.ReactNode;
  /** Description of the graph */
  graphDescription?: string | React.ReactNode;
  /** Footnote for the graph */
  footNote?: string | React.ReactNode;
  /** Source data for the graph */
  sources?: SourcesDataType[];

  // Colors and Styling
  /** Background color of the graph */
  backgroundColor?: string | boolean;
  /** Defines the layout for the cards */
  layout?: 'primary' | 'secondary';
  /** Font size of the main text */
  headingFontSize?: string;
  /** Padding around the graph. Defaults to 0 if no backgroundColor is mentioned else defaults to 1rem */
  padding?: string;
  /** Toggle the fill color of the main text. */
  textBackground?: boolean;
  /** Toggle is the text is center aligned. */
  centerAlign?: boolean;
  /** Vertical alignment of the main text */
  verticalAlign?: 'center' | 'top' | 'bottom';
  /** Custom styles for the graph. Each object should be a valid React CSS style object. */
  styles?: StyleObject;
  /** Custom class names */
  classNames?: ClassNameObject;

  // Values and Ticks
  /** Configuration options for controlling number formatting, localization, prefixes/suffixes, precision, and zero padding. */
  numberDisplayOptions?: NumberFormatOptions;
  /** Defines how “NA” values should be displayed/labelled in the graph */
  naLabel?: string;
  /** Main text */
  value: number | string;
  /** Sub text next to main text */
  year?: number | string;

  // Configuration and Options
  /** Language setting  */
  language?: Languages;
  /** Color theme */
  theme?: 'light' | 'dark';
  /** Unique ID for the graph */
  graphID?: string;
}

export function BasicStatCard(props: Props) {
  const {
    year,
    value,
    graphTitle,
    sources,
    graphDescription,
    footNote,
    padding,
    backgroundColor = false,
    graphID,
    language = 'en',
    theme = 'light',
    textBackground = false,
    headingFontSize = '4.375rem',
    centerAlign = false,
    verticalAlign = 'center',
    layout = 'primary',
    styles,
    classNames,
    numberDisplayOptions,
    naLabel = 'NA',
  } = props;
  return (
    <div className={`${theme || 'light'} w-full`}>
      <div
        id={graphID}
        dir={language === 'he' || language === 'ar' ? 'rtl' : undefined}
        className={cn(
          `${theme || 'light'} flex flex-col w-full box-border h-full ${
            !backgroundColor ? 'bg-transparent' : backgroundColor === true ? 'bg-surface' : ''
          } ${language || 'en'}`,
          classNames?.graphContainer,
        )}
        style={{
          ...(styles?.graphContainer || {}),
          ...(backgroundColor && backgroundColor !== true ? { backgroundColor } : {}),
          padding: backgroundColor ? padding || '1rem' : padding || 0,
        }}
      >
        <div
          className={`flex flex-col w-full grow ${layout !== 'secondary' ? 'gap-12 justify-between' : 'gap-4 justify-center'}`}
        >
          {layout !== 'secondary' ? (
            <>
              {graphTitle || graphDescription ? (
                <GraphHeader
                  styles={{
                    title: styles?.title,
                    description: styles?.description,
                  }}
                  classNames={{
                    title: classNames?.title,
                    description: classNames?.description,
                  }}
                  graphTitle={graphTitle}
                  graphDescription={graphDescription}
                />
              ) : null}
              <div
                className={`flex flex-col justify-between grow ${
                  verticalAlign === 'top'
                    ? 'justify-start'
                    : verticalAlign === 'bottom'
                      ? 'justify-end'
                      : 'justify-center'
                }`}
              >
                <H3
                  marginBottom='base'
                  className={`leading-xs [-webkit-text-stroke:2px_var(--content-primary)] text-shadow-none font-heading ${
                    centerAlign
                      ? 'text-center'
                      : language === 'he' || language === 'ar'
                        ? 'text-right'
                        : 'text-left'
                  } ${textBackground ? 'text-content-primary' : 'text-transparent'}`}
                  style={{
                    fontSize: headingFontSize,
                    letterSpacing: '0.05rem',
                  }}
                >
                  {numberFormattingFunction(
                    value,
                    naLabel,
                    numberDisplayOptions?.precision ?? 2,
                    numberDisplayOptions?.prefix,
                    numberDisplayOptions?.suffix,
                    numberDisplayOptions?.locale,
                    numberDisplayOptions?.padZeros,
                  )}{' '}
                  {year ? (
                    <span
                      className='text-lg font-normal mt-0 mb-4 text-content-secondary'
                      style={{
                        marginLeft: '-8px',
                        lineHeight: '1.09',
                        textShadow: 'none',
                        WebkitTextStrokeWidth: 0,
                      }}
                    >
                      ({year})
                    </span>
                  ) : null}
                </H3>
              </div>
              {sources || footNote ? (
                <GraphFooter
                  styles={{ footnote: styles?.footnote, source: styles?.source }}
                  classNames={{
                    footnote: classNames?.footnote,
                    source: classNames?.source,
                  }}
                  sources={sources}
                  footNote={footNote}
                />
              ) : null}
            </>
          ) : (
            <>
              <H3
                marginBottom={layout === 'secondary' ? 'none' : 'base'}
                className={`leading-xs [-webkit-text-stroke:2px_var(--content-primary)] text-shadow-none font-heading ${
                  centerAlign
                    ? 'text-center'
                    : language === 'he' || language === 'ar'
                      ? 'text-right'
                      : 'text-left'
                } ${textBackground ? 'text-content-primary' : 'text-transparent'}`}
                style={{
                  fontSize: headingFontSize,
                  letterSpacing: '0.05rem',
                }}
              >
                {numberFormattingFunction(
                  value,
                  naLabel,
                  numberDisplayOptions?.precision ?? 2,
                  numberDisplayOptions?.prefix,
                  numberDisplayOptions?.suffix,
                  numberDisplayOptions?.locale,
                  numberDisplayOptions?.padZeros,
                )}{' '}
                {year ? (
                  <span
                    className='text-lg font-normal mt-0 mb-4 text-content-secondary'
                    style={{
                      marginLeft: '-8px',
                      lineHeight: '1.09',
                      textShadow: 'none',
                      WebkitTextStrokeWidth: 0,
                    }}
                  >
                    ({year})
                  </span>
                ) : null}
              </H3>
              {graphTitle || graphDescription ? (
                <GraphHeader
                  styles={{
                    title: styles?.title,
                    description: styles?.description,
                  }}
                  classNames={{
                    title: classNames?.title,
                    description: classNames?.description,
                  }}
                  graphTitle={graphTitle}
                  graphDescription={graphDescription}
                />
              ) : null}
              {sources || footNote ? (
                <GraphFooter
                  styles={{ footnote: styles?.footnote, source: styles?.source }}
                  classNames={{
                    footnote: classNames?.footnote,
                    source: classNames?.source,
                  }}
                  sources={sources}
                  footNote={footNote}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
